import styled from "styled-components";
import { Select, radius } from "@libertify/ui";
import type { GlobalToken } from "antd";

/**
 * Multi-value select rendered as teal pill chips. The lib `Select` leaves
 * `mode="multiple"` / `mode="tags"` chips at antd's default gray and pins the
 * control to its single-line height; theme the chips and let the control grow
 * as they wrap. Base for both the free-entry tags field and the goals select.
 */
export const ChipsSelect = styled(Select)<{ $token: GlobalToken }>`
  /* styled(Select) puts this class on the .ant-select root, so all overrides are
     descendant selectors from it, and colors need !important to beat antd's own
     multiple-mode chip styles (injection order otherwise wins). */
  &.ant-select {
    height: auto;
  }

  &.ant-select .ant-select-selector {
    height: auto !important;
    min-height: 40px;
    padding-block: 3px !important;
    align-items: center;
  }

  .ant-select-selection-item {
    display: inline-flex;
    align-items: center;
    height: 26px !important;
    line-height: 1 !important;
    padding-inline: 10px;
    border-radius: ${radius.pill}px !important;
    background: ${({ $token }) => $token.colorPrimaryBg} !important;
    border: 1px solid ${({ $token }) => $token.colorPrimaryBorder} !important;
    color: ${({ $token }) => $token.colorText} !important;
  }

  .ant-select-selection-item-remove {
    display: inline-flex;
    color: ${({ $token }) => $token.colorTextTertiary} !important;

    &:hover {
      color: ${({ $token }) => $token.colorText} !important;
    }
  }
`;

/**
 * Free-entry tags field: the chip styling above plus a persistent "Add a tag…"
 * hint at the end of the list (antd's own placeholder only shows on the empty
 * state). `$placeholder` is the localized hint text; it's hidden while the
 * field is focused so it never collides with what the user is typing.
 */
export const TagsSelect = styled(ChipsSelect)<{ $placeholder: string }>`
  &.ant-select:not(.ant-select-focused) .ant-select-selection-overflow::after {
    content: "${({ $placeholder }) => $placeholder}";
    align-self: center;
    color: ${({ $token }) => $token.colorTextTertiary};
    pointer-events: none;
    white-space: nowrap;
  }
`;

export const SectionHeading = styled.h4`
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 700;
`;

export const SectionSubtitle = styled.p<{ $token: GlobalToken }>`
  margin: 0 0 16px;
  font-size: ${({ $token }) => $token.fontSizeSM}px;
  color: ${({ $token }) => $token.colorTextTertiary};
`;

export const SectionDivider = styled.div<{ $token: GlobalToken }>`
  height: 1px;
  margin: 24px 0;
  background: ${({ $token }) => $token.colorBorderSecondary};
`;

export const ControlsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const FieldBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
